/** The 400 of a catalog create / update (`ConsoleCatalogWriteError`), split into
 *  what a form shows where. The body is either field-keyed messages
 *  (`{"name": "Name is required."}`) or `detail` + `code` about the whole request
 *  (`malformed_json`, `activity_not_saved`, `itinerary_not_saved`, and a category's
 *  refused icon file: `image_*` / `svg_active_content`, whose shared upload-check
 *  body also repeats `detail` as `message`). `detail`, `message` and `code` are
 *  never fields: read as field errors they would surface as phantom "detail" /
 *  "message" / "code" fields, or put a request-level message under whichever input
 *  happened to read the body.
 *
 *  The catalog records' immediate panels refuse the same way (an activity's documents,
 *  `ConsoleActivityDocumentError`): `detail` + `code`, and a `field` naming the key it
 *  concerns (`file` for the upload) — that field's error, never a field called "field".
 *  A file over the upload cap is a 413 with the shared `{detail, message, code}` body
 *  (`upload_too_large`): a request-level message like a 400's. */
import { ConsoleApiError } from './client';

export interface CatalogWriteErrors {
  /** The request-level message (`detail`, else its copy `message`): the form's own
   *  error, under no field. */
  form: string | null;
  /** Field-keyed messages, by field name. Never `detail`, `message` or `code`. */
  fields: Readonly<Record<string, string>>;
}

const REQUEST_KEYS: ReadonlySet<string> = new Set(['detail', 'message', 'code', 'field']);

/** A message as DRF sends it: a string, or a list whose first string is the message. */
function message(v: unknown): string | null {
  if (typeof v === 'string') return v || null;
  if (Array.isArray(v)) return v.find((x): x is string => typeof x === 'string' && x !== '') ?? null;
  return null;
}

/** The split of a catalog write's 400 (or an upload's 413), or null for any other error. */
export function catalogWriteErrors(e: unknown): CatalogWriteErrors | null {
  if (!(e instanceof ConsoleApiError) || (e.status !== 400 && e.status !== 413)) return null;
  // Typed as an object, but a proxy's HTML error page arrives as a string.
  const raw: unknown = e.body;
  const body = (raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {}) as Record<string, unknown>;
  const fields: Record<string, string> = {};
  for (const [key, value] of Object.entries(body)) {
    if (REQUEST_KEYS.has(key)) continue;
    const msg = message(value);
    if (msg) fields[key] = msg;
  }
  const detail = message(body.detail) ?? message(body.message);
  // A refusal that names its key is that field's error, not the form's.
  const named = typeof body.field === 'string' && body.field !== '' ? body.field : null;
  if (named && detail) return { form: null, fields: { ...fields, [named]: detail } };
  return { form: detail, fields };
}
